import { useCallback, useRef, useState } from "react";

import {
  Avatar,
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
} from "@fluentui/react-components";
import { ArrowMaximize24Regular, ArrowMinimize24Regular, Dismiss24Regular } from "@fluentui/react-icons";
import { useAtom, useAtomValue } from "jotai";
import { MUTATION_KEYS } from "library/shared";
import { genUUID } from "library/utils";
import { useTranslation } from "react-i18next";
import { useIsMutating } from "react-query";
import { chatHistoryAtom, chatIdAtom, chatPanelOpenAtom } from "store";
import styled from "styled-components";

import { Flex } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";
import useEmployee from "hooks/useEmployee";
import { useIsMobile } from "hooks/useIsMobile";

import parse from "html-react-parser";

import { ChatIcon } from "./ChatIcon";
import NewMessage from "./NewMessage";

export function ChatPanel() {
  const theme = useAppTheme();
  const [chatPanelOpen, setChatPanelOpen] = useAtom(chatPanelOpenAtom);
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { employeeData } = useEmployee();
  const [isExpanded, setIsExpanded] = useState(false);

  const historyContainerRef = useRef<HTMLDivElement | null>(null);

  const [newMessageState, setNewMessageState] = useState({
    message: "",
    sent: false,
  });

  const chatHistory = useAtomValue(chatHistoryAtom);

  const chatSessionId = useAtomValue(chatIdAtom);

  const handleChatPanelOpen = useCallback((state) => {
    setChatPanelOpen(state);
  }, []);

  const waitingForChatbotResponse = useIsMutating({
    mutationKey: MUTATION_KEYS.sendMessageToChatbot,
  });

  const handleUpdateNewMessageState = useCallback((state) => {
    return setNewMessageState(state);
  }, []);

  return (
    <>
      <OverlayDrawer
        position="end"
        // size="small"
        open={chatPanelOpen}
        onOpenChange={() => {
          setChatPanelOpen(false);
        }}
        style={{
          width: isMobile ? "100%" : isExpanded ? "900px" : "450px",
          transition: "width 0.3s ease",
        }}
      >
        <StyledHeader>
          <DrawerHeaderTitle
            style={{
              paddingRight: "20px",
            }}
            action={
              <Flex wrap="nowrap" align="center">
                {!isMobile && (
                  <Button
                    appearance="subtle"
                    aria-label={isExpanded ? "Collapse" : "Expand"}
                    icon={isExpanded ? <ArrowMinimize24Regular /> : <ArrowMaximize24Regular />}
                    onClick={() => setIsExpanded((prev) => !prev)}
                  />
                )}
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  onClick={() => {
                    handleChatPanelOpen(false);
                  }}
                />
              </Flex>
            }
          ></DrawerHeaderTitle>
        </StyledHeader>

        <StyledBody>
          {!chatSessionId && (
            <BodyLoadingLayout>
              <LoadingWrap noMarginLeft />
            </BodyLoadingLayout>
          )}

          <Flex direction="column" width={"100%"} $gap={"20px"}>
            <Flex wrap="nowrap" align="flex-end" width={"100%"}>
              <ChatIcon />
              <BotMessage expanded={isExpanded} style={{ textAlign: "left" }}>{t("hbh.botHello.label")}</BotMessage>
            </Flex>
            {(chatHistory || [])?.map((history, index) => {
          
              const parsedPrevAnswer =parse(history?.PrevAnswer || "");
              return (
                <>
                  {history?.PrevQuestion ? (
                    <Flex
                      wrap="nowrap"
                      $justify="flex-end"
                      align="flex-end"
                      width={"100%"}
                      key={genUUID() + index}
                    >
                      <UserMessage expanded={isExpanded}>{history?.PrevQuestion}</UserMessage>
                      <Avatar
                        image={{
                          src: employeeData?.backgroundPicturePath,
                        }}
                        style={{
                          width: 42,
                          height: 42,
                          border: `1px solid ${theme.palette.themePrimary}`,
                        }}
                      />
                    </Flex>
                  ) : null}
                  {history?.PrevAnswer ? (
                    <Flex wrap="nowrap" align="flex-end" width={"100%"} key={genUUID() + index}>
                      <ChatIcon />
                      <BotMessage expanded={isExpanded} style={{ textAlign: "left" }}>{parsedPrevAnswer}</BotMessage>
                    </Flex>
                  ) : null}
                </>
              );
            })}
            {newMessageState.sent ? (
              <Flex
                wrap="nowrap"
                align="flex-end"
                $justify="flex-end"
                width={"100%"}
              >
                <UserMessage expanded={isExpanded}>{newMessageState.message}</UserMessage>
                <Avatar
                  image={{
                    src: employeeData?.backgroundPicturePath,
                  }}
                  style={{
                    width: 42,
                    height: 42,
                    border: `1px solid ${theme.palette.themePrimary}`,
                  }}
                />
              </Flex>
            ) : null}
            {waitingForChatbotResponse ? (
              <Flex wrap="nowrap" align="baseline" width={"100%"}>
                <ChatIcon />
                <LoadingWrap />
              </Flex>
            ) : null}
            <div ref={historyContainerRef} />
          </Flex>
        </StyledBody>
        <StyledFooter>
          <NewMessage
            historyContainerRef={historyContainerRef}
            newMessageState={newMessageState}
            setNewMessageState={handleUpdateNewMessageState}
          />
          <DisclaimerSection>
            <DisclaimerText>
              <strong>{t("hbh.aiDisclaimerTitle.label")}</strong>{": "}{t("hbh.aiDisclaimer.label")}
            </DisclaimerText>
          </DisclaimerSection>
        </StyledFooter>
      </OverlayDrawer>
    </>
  );
}

const MessageBox = styled.div<{expanded?: boolean}>`
  box-sizing: border-box;
  height: fit-content;
  min-height: 60px;
  width: fit-content;
  max-width: ${({ expanded }) => (expanded ? "650px" : "325px")};
  display: flex;
  font-size: 14px;
  align-items: center;
  border-radius: 12px;
  padding: 10px 15px;
  word-break: break-word;
  svg {
    width: 30px;
    height: 30px;
  }
`;

const BotMessage = styled.div<{ expanded?: boolean }>`
  box-sizing: border-box;
  height: fit-content;
  min-height: 60px;
  word-break: break-word;

  max-width: ${({ expanded }) => (expanded ? "650px" : "325px")};
  font-size: 14px;
  border-radius: 12px;
  padding: 10px 15px;

  svg {
    width: 30px;
    height: 30px;
  }

  border-bottom-left-radius: 0;
  background: ${({ theme }) => theme.palette.neutralLighter};
`;

const UserMessage = styled(MessageBox)`
  text-align: right;
  justify-content: flex-end;

  border-bottom-right-radius: 0;
  background: ${({ theme }) => theme.palette.purpleDark};
  color: ${({ theme }) => theme.palette.white};
`;

const BodyLoadingLayout = styled.div`
  display: flex;
  position: absolute;
  z-index: 1;
  opacity: 0.7;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  justify-content: center;
  align-items: center;
`;

const StyledHeader = styled(DrawerHeader)<{ bgImage?: string }>`
  padding: 10px;

  background: ${({ theme }) => theme.palette.white};
  color: ${({ theme }) => theme.palette.black};

  // border-bottom: 1px solid ${({ theme }) => theme.palette.neutralLighter};
`;

const StyledBody = styled(DrawerBody)`
  position: relative;
  padding: 10px;
  // background-color: ${({ theme }) => theme.palette.neutralLighter};
  background-color: ${({ theme }) => theme.palette.white};
  background-size: calc(15px) calc(15px);
`;

const StyledFooter = styled(DrawerFooter)`
  display: flex;
  flex-direction: column;
  padding: 10px 10px 20px;
`;

const DisclaimerSection = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  display: flex;
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme }) => theme.palette.neutralLighterAlt};
  border-left: 3px solid ${({ theme }) => theme.palette.neutralTertiary};
`;

const DisclaimerText = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.palette.neutralSecondary};
  line-height: 1.4;
`;

const LoadingWrap = styled.div<{ noMarginLeft?: boolean }>`
  width: 15px;
  aspect-ratio: 1;
  border-radius: 50%;
  animation: l5 1s infinite linear alternate;
  margin-top: 10px;
  margin-left: ${({ noMarginLeft }) => (noMarginLeft ? "0px" : "50px")};
  @keyframes l5 {
    0% {
      box-shadow:
        20px 0 ${({ theme }) => theme.palette.neutralLight},
        -20px 0 #0002;
      background: ${({ theme }) => theme.palette.neutralLight};
    }
    33% {
      box-shadow:
        20px 0 ${({ theme }) => theme.palette.neutralLight},
        -20px 0 #0002;
      background: #0002;
    }
    66% {
      box-shadow:
        20px 0 #0002,
        -20px 0 ${({ theme }) => theme.palette.neutralLight};
      background: #0002;
    }
    100% {
      box-shadow:
        20px 0 #0002,
        -20px 0 ${({ theme }) => theme.palette.neutralLight};
      background: ${({ theme }) => theme.palette.neutralLight};
    }
  }
`;
